import React from 'react';
import CommodityChart from '../Charts/CommodityChart';

const CommodityTab = ({ showDetails, openDrillDown }) => {
  const commodityData = [
    { id: 'containerized', name: 'Containerized', current: '85,420 T', previous: '75,920 T', growth: '+12.5%', status: 'high' },
    { id: 'dry-bulk', name: 'Dry Bulk', current: '62,150 T', previous: '57,420 T', growth: '+8.2%', status: 'high' },
    { id: 'liquid-bulk', name: 'Liquid Bulk', current: '28,360 T', previous: '30,920 T', growth: '-8.2%', status: 'low' },
    { id: 'break-bulk', name: 'Break Bulk', current: '9,500 T', previous: '9,030 T', growth: '+5.2%', status: 'medium' }
  ];

  const commodities = [
    {
      id: 'containerized',
      name: 'Containerized Cargo',
      icon: 'fas fa-box',
      color: '#4da6ff',
      value: '85,420',
      share: '46.1%',
      change: '+12.5%',
      isPositive: true,
      topRoutes: 'Asia-Europe, Trans-Pacific',
      mainProducts: 'Electronics (32%), Apparel (28%), Machinery (22%)'
    },
    {
      id: 'dry-bulk',
      name: 'Dry Bulk',
      icon: 'fas fa-mountain',
      color: '#ff9f40',
      value: '62,150',
      share: '33.5%',
      change: '+8.2%',
      isPositive: true,
      topRoutes: 'Australia-Asia, South America-Europe',
      mainProducts: 'Iron Ore (45%), Coal (30%), Grain (15%)'
    },
    {
      id: 'liquid-bulk',
      name: 'Liquid Bulk',
      icon: 'fas fa-oil-can',
      color: '#ff6384',
      value: '28,360',
      share: '15.3%',
      change: '-8.2%',
      isPositive: false,
      topRoutes: 'Middle East-Asia, West Africa-Europe',
      mainProducts: 'Crude Oil (60%), Chemicals (25%), LNG (10%)'
    },
    {
      id: 'break-bulk',
      name: 'Break Bulk',
      icon: 'fas fa-cubes',
      color: '#4cd964',
      value: '9,500',
      share: '5.1%',
      change: '+5.2%',
      isPositive: true,
      topRoutes: 'Regional, Short Sea Shipping',
      mainProducts: 'Steel (40%), Timber (25%), Project Cargo (20%)'
    }
  ];

  const getStatusClass = (status) => {
    switch(status) {
      case 'high': return 'status-indicator status-high';
      case 'medium': return 'status-indicator status-medium';
      case 'low': return 'status-indicator status-low';
      default: return 'status-indicator';
    }
  };

  // Handle commodity card click
  const handleCommodityClick = (commodity) => {
    if (openDrillDown) {
      openDrillDown('commodity', {
        title: `${commodity.name} Analysis`,
        icon: commodity.icon,
        iconColor: commodity.color,
        category: 'Commodity Performance',
        summary: `${commodity.name} handled ${commodity.value} tons this month, ${commodity.change} from last month.`,
        details: `This represents ${commodity.share} of total port throughput. Key growth drivers include increased trade volumes and efficient handling operations.`,
        keyMetrics: [
          { label: 'Current Month', value: `${commodity.value} T`, change: commodity.change },
          { label: 'Market Share', value: commodity.share, change: commodity.isPositive ? '+2.1%' : '-1.5%' },
          { label: 'Top Routes', value: commodity.topRoutes.split(',')[0], change: 'Primary' },
          { label: 'Main Products', value: commodity.mainProducts.split('(')[0].trim(), change: 'Leading' }
        ],
        commodityDetails: {
          'Commodity Type': commodity.name,
          'Monthly Volume': `${commodity.value} T`,
          'Market Share': commodity.share,
          'Growth Rate': commodity.change,
          'Performance': commodity.isPositive ? 'Strong' : 'Needs Attention',
          'Primary Routes': commodity.topRoutes,
          'Key Products': commodity.mainProducts
        },
        performanceAnalysis: commodity.isPositive ? 
          `Strong performance with ${commodity.change} growth. Leading contributor to port throughput.` :
          `Performance declined by ${commodity.change.replace('-', '')}. Market conditions affecting demand.`,
        growthDrivers: commodity.isPositive ? [
          'Increased manufacturing output',
          'E-commerce expansion',
          'Efficient port operations',
          'Favorable trade agreements'
        ] : [
          'Market price volatility',
          'Supply chain disruptions',
          'Seasonal factors',
          'Competition from alternative ports'
        ],
        recommendations: [
          'Maintain current operational efficiency',
          'Explore new trade routes',
          'Invest in specialized handling equipment',
          'Strengthen customer relationships'
        ],
        forecast: `Expected to ${commodity.isPositive ? 'grow' : 'stabilize'} in next quarter with ${commodity.isPositive ? '+8-10%' : '±2%'} change`
      }, `${commodity.name} Analysis`);
    }
  };

  // Handle table row click
  const handleTableRowClick = (commodity) => {
    if (openDrillDown) {
      openDrillDown('commodity-performance', {
        title: `${commodity.name} Performance Metrics`,
        icon: 'fas fa-chart-line',
        iconColor: commodity.status === 'high' ? '#4cd964' : commodity.status === 'medium' ? '#ff9f40' : '#ff6384',
        category: 'Performance Analysis',
        summary: `${commodity.name} showed ${commodity.growth} growth this month, handling ${commodity.current}.`,
        details: `Performance comparison shows ${commodity.current} vs ${commodity.previous} last month. Market position is ${commodity.status}.`,
        performanceMetrics: [
          { metric: 'Current Month', value: commodity.current, benchmark: 'Port Average' },
          { metric: 'Previous Month', value: commodity.previous, benchmark: 'Historical' },
          { metric: 'Growth Rate', value: commodity.growth, benchmark: commodity.status === 'high' ? 'Excellent' : commodity.status === 'medium' ? 'Average' : 'Below Average' },
          { metric: 'Market Position', value: commodity.status.charAt(0).toUpperCase() + commodity.status.slice(1), benchmark: 'Ranking' }
        ],
        trendAnalysis: `Showing ${commodity.growth.includes('+') ? 'positive' : 'negative'} trend. ${commodity.growth.includes('+') ? 'Exceeding' : 'Below'} port average growth of +7.8%.`,
        contributingFactors: commodity.growth.includes('+') ? [
          'Increased demand from key markets',
          'Improved handling efficiency',
          'Competitive pricing',
          'Reliable service'
        ] : [
          'Market saturation',
          'Competitive pressure',
          'Operational challenges',
          'Seasonal decline'
        ],
        actionItems: commodity.growth.includes('+') ? [
          'Capitalize on growth momentum',
          'Expand capacity if trend continues',
          'Strengthen market position',
          'Invest in technology for further efficiency'
        ] : [
          'Analyze market conditions',
          'Review pricing strategy',
          'Improve operational efficiency',
          'Explore new market opportunities'
        ]
      }, `${commodity.name} Performance`);
    }
  };

  return (
    <div id="commodity-tab" className="tab-content-wrapper">
      {/* Insights Container - Updated with proper data structure */}
      <div className="insights-container">
        <div 
          className="insight-card clickable"
          onClick={() => openDrillDown && openDrillDown('insight', {
            title: 'Containerized Dominance',
            icon: 'fas fa-box',
            iconColor: '#4da6ff',
            category: 'Market Leadership',
            summary: 'Containerized cargo represents 46.1% of total throughput, showing strongest growth at 12.5% YoY.',
            details: 'This dominance is driven by e-commerce expansion and increased manufacturing output. Containerized cargo is expected to maintain leadership position through 2024.',
            keyMetrics: [
              { label: 'Market Share', value: '46.1%', change: '+3.2%' },
              { label: 'Monthly Volume', value: '85,420 T', change: '+12.5%' },
              { label: 'Revenue Contribution', value: '$45.2M', change: '+15.8%' },
              { label: 'Growth Rate', value: 'Industry Leader', change: '+5.2% above average' }
            ],
            marketAnalysis: {
              'Position': 'Market Leader',
              'Competitive Advantage': 'Efficiency & Technology',
              'Customer Base': 'Diversified',
              'Future Outlook': 'Strong Growth Expected'
            },
            growthDrivers: [
              'E-commerce expansion driving container demand',
              'Manufacturing recovery increasing exports',
              'Efficient container handling operations',
              'Strategic location for transshipment'
            ],
            competitiveAdvantages: [
              'Fastest turnaround time: 16.2 hours',
              'Advanced container tracking technology',
              'Dedicated container terminal',
              'Experienced workforce'
            ],
            challenges: [
              'Increasing competition from regional ports',
              'Capacity constraints during peak seasons',
              'Need for technology upgrades',
              'Labor availability'
            ],
            strategicInitiatives: [
              'Expand container yard capacity by 20%',
              'Implement AI-based container management',
              'Develop cold chain facilities',
              'Enhance intermodal connectivity'
            ],
            financialImpact: 'Generates 48% of port revenue, $520M annually',
            sustainabilityGoals: [
              'Reduce carbon footprint by 25% by 2025',
              'Implement electric container handling equipment',
              'Optimize container stacking to reduce moves'
            ],
            timeline: 'Q4 2024 for capacity expansion completion',
            confidence: 'High (based on current contracts and market trends)'
          }, 'Containerized Cargo Analysis')}
        >
          <div className="insight-title"><i className="fas fa-box"></i> Containerized Dominance</div>
          <div className="insight-content">Containerized cargo represents 46.1% of total throughput, showing strongest growth at 12.5% YoY.</div>
        </div>
        
        <div 
          className="insight-card success clickable"
          onClick={() => openDrillDown && openDrillDown('insight', {
            title: 'Dry Bulk Recovery',
            icon: 'fas fa-mountain',
            iconColor: '#ff9f40',
            category: 'Market Recovery',
            summary: 'Dry bulk shipments recovered with 8.2% growth after last year\'s decline due to new mining contracts.',
            details: 'The recovery is driven by renewed mining activities and infrastructure projects. Iron ore and coal shipments are leading the growth.',
            keyMetrics: [
              { label: 'Monthly Volume', value: '62,150 T', change: '+8.2%' },
              { label: 'Market Share', value: '33.5%', change: '+1.8%' },
              { label: 'Revenue Growth', value: '+12.5%', change: 'Quarterly' },
              { label: 'Contract Volume', value: 'New $150M', change: 'Contracts signed' }
            ],
            recoveryFactors: [
              'New iron ore contracts with Australian miners',
              'Infrastructure projects driving demand',
              'Agricultural exports increasing',
              'Improved bulk handling efficiency'
            ],
            commodityBreakdown: [
              { commodity: 'Iron Ore', share: '45%', growth: '+12.5%' },
              { commodity: 'Coal', share: '30%', growth: '+6.8%' },
              { commodity: 'Grain', share: '15%', growth: '+9.2%' },
              { commodity: 'Cement', share: '6%', growth: '+4.5%' }
            ],
            marketOpportunities: [
              'Infrastructure development in emerging markets',
              'Renewable energy materials (rare earth minerals)',
              'Agricultural exports expansion',
              'Specialized bulk cargo handling'
            ],
            operationalImprovements: [
              'Bulk terminal automation',
              'Dust suppression systems',
              'Loading efficiency optimization',
              'Storage capacity expansion'
            ],
            financialImpact: 'Expected to generate $280M revenue in 2024',
            riskFactors: [
              'Commodity price volatility',
              'Geopolitical trade restrictions',
              'Weather affecting agricultural exports',
              'Environmental regulations'
            ],
            mitigationStrategies: [
              'Diversify commodity mix',
              'Long-term contracts with price stability',
              'Invest in weather-protected storage',
              'Compliance with environmental standards'
            ],
            timeline: 'Full recovery expected by Q3 2024',
            confidence: 'Medium-High (dependent on commodity prices)'
          }, 'Dry Bulk Recovery Analysis')}
        >
          <div className="insight-title"><i className="fas fa-mountain"></i> Dry Bulk Recovery</div>
          <div className="insight-content">Dry bulk shipments recovered with 8.2% growth after last year's decline due to new mining contracts.</div>
        </div>
        
        <div 
          className="insight-card warning clickable"
          onClick={() => openDrillDown && openDrillDown('insight', {
            title: 'Liquid Bulk Decline',
            icon: 'fas fa-oil-can',
            iconColor: '#ff6384',
            category: 'Market Trend',
            summary: 'Liquid bulk decreased by 8.2% due to reduced crude oil imports and shift to renewable energy sources.',
            details: 'The decline reflects global energy transition trends and reduced dependence on traditional fossil fuels. LNG and chemical shipments show potential for recovery.',
            keyMetrics: [
              { label: 'Monthly Volume', value: '28,360 T', change: '-8.2%' },
              { label: 'Market Share', value: '15.3%', change: '-2.1%' },
              { label: 'Revenue Impact', value: '-$8.5M', change: 'Quarterly' },
              { label: 'Crude Oil Share', value: '60%', change: '-15%' }
            ],
            declineFactors: [
              'Reduced crude oil imports from Middle East',
              'Shift to renewable energy sources',
              'Geopolitical trade restrictions',
              'Increased domestic production alternatives'
            ],
            commodityAnalysis: [
              { type: 'Crude Oil', trend: 'Declining', share: '60%' },
              { type: 'Chemicals', trend: 'Stable', share: '25%' },
              { type: 'LNG', trend: 'Growing', share: '10%' },
              { type: 'Vegetable Oil', trend: 'Stable', share: '5%' }
            ],
            growthOpportunities: [
              'LNG terminal development',
              'Chemical diversification',
              'Biofuels handling',
              'Specialized liquid cargo'
            ],
            strategicActions: [
              'Develop LNG handling capabilities',
              'Attract chemical manufacturers',
              'Invest in specialized storage tanks',
              'Explore biofuels market'
            ],
            marketTransition: 'Moving from crude oil dominance to diversified liquid portfolio',
            financialProjection: 'Expected to stabilize at $120M annual revenue',
            riskAssessment: [
              'Continued decline in crude oil',
              'High investment requirements for diversification',
              'Market competition for LNG',
              'Environmental compliance costs'
            ],
            recoveryPlan: [
              'Complete LNG terminal by Q2 2025',
              'Secure 3 new chemical contracts',
              'Implement $50M infrastructure upgrade',
              'Develop biofuels expertise'
            ],
            timeline: 'Transition period: 18-24 months',
            confidence: 'Medium (requires strategic investment)'
          }, 'Liquid Bulk Market Analysis')}
        >
          <div className="insight-title"><i className="fas fa-oil-can"></i> Liquid Bulk Decline</div>
          <div className="insight-content">Liquid bulk decreased by 8.2% due to reduced crude oil imports and shift to renewable energy sources.</div>
        </div>
      </div>

      <div className="commodity-details">
        {commodities.map((commodity) => (
          <div 
            className="commodity-card clickable" 
            key={commodity.id}
            onClick={() => handleCommodityClick(commodity)}
          >
            <div className="commodity-header">
              <div 
                className="commodity-icon" 
                style={{ backgroundColor: commodity.color }}
              >
                <i className={commodity.icon}></i>
              </div>
              <div className="commodity-name">{commodity.name}</div>
            </div>
            
            <div className="commodity-stats">
              <div className="stat-item">
                <div className="stat-value">{commodity.value}</div>
                <div className="stat-label">This Month</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{commodity.share}</div>
                <div className="stat-label">Share of Total</div>
              </div>
            </div>
            
            <div 
              className={`card-change ${commodity.isPositive ? 'positive' : 'negative'}`} 
              style={{ marginTop: '15px' }}
            >
              <i className={`fas fa-arrow-${commodity.isPositive ? 'up' : 'down'}`}></i>
              <span>{commodity.change} {commodity.isPositive ? 'increase' : 'decrease'} from last month</span>
            </div>
            
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: commodity.share, 
                  backgroundColor: commodity.color 
                }}
              ></div>
            </div>
            
            <div style={{ marginTop: '15px', fontSize: '14px', color: '#666' }}>
              <div>
                <i className="fas fa-ship" style={{ marginRight: '8px' }}></i>
                <strong>Top Routes:</strong> {commodity.topRoutes}
              </div>
              <div>
                <i className="fas fa-industry" style={{ marginRight: '8px' }}></i>
                <strong>Main Products:</strong> {commodity.mainProducts}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="charts-section">
        <div className="chart-container">
          <h3 className="section-title">
            <i className="fas fa-chart-line"></i>
            <span>Commodity Distribution Trend</span>
          </h3>
          <div style={{height: '400px'}}>
            <CommodityChart 
              onClick={() => openDrillDown && openDrillDown('commodity-trend', {
                title: 'Commodity Distribution Trend Analysis',
                icon: 'fas fa-chart-line',
                iconColor: '#4da6ff',
                category: 'Market Analysis',
                summary: 'Containerized cargo maintains dominance while liquid bulk shows decline over the past 12 months.',
                details: 'Trend analysis shows shifting commodity mix with containerized growth offsetting liquid bulk decline.',
                trendData: [
                  { period: 'Jan 2023', containerized: '42%', dryBulk: '30%', liquidBulk: '22%', breakBulk: '6%' },
                  { period: 'Current', containerized: '46.1%', dryBulk: '33.5%', liquidBulk: '15.3%', breakBulk: '5.1%' }
                ],
                keyObservations: [
                  'Containerized share increased by 4.1%',
                  'Liquid bulk declined by 6.7%',
                  'Dry bulk gained 3.5% market share',
                  'Overall throughput increased by 12.5%'
                ],
                marketImplications: [
                  'Port becoming more container-focused',
                  'Need for liquid bulk diversification',
                  'Dry bulk recovery supporting growth',
                  'Balanced commodity mix important for stability'
                ],
                strategicRecommendations: [
                  'Maintain container handling leadership',
                  'Develop liquid bulk alternatives',
                  'Capitalize on dry bulk recovery',
                  'Monitor commodity mix regularly'
                ]
              }, 'Commodity Trend Analysis')}
            />
          </div>
        </div>
        
        <div className="table-container">
          <h3 className="section-title">
            <i className="fas fa-boxes"></i>
            <span>Top Performing Commodities</span>
          </h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Commodity</th>
                <th>This Month</th>
                <th>Last Month</th>
                <th>Growth</th>
              </tr>
            </thead>
            <tbody>
              {commodityData.map((commodity, index) => (
                <tr 
                  key={index}
                  className="clickable-row"
                  onClick={() => handleTableRowClick(commodity)}
                >
                  <td><strong>{commodity.name}</strong></td>
                  <td>{commodity.current}</td>
                  <td>{commodity.previous}</td>
                  <td><span className={getStatusClass(commodity.status)}>{commodity.growth}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="table-container" style={{marginTop: '30px'}}>
        <h3 className="section-title">
          <i className="fas fa-table"></i>
          <span>Category-wise Quarterly Performance (Tons)</span>
        </h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Q1 2023</th>
              <th>Q2 2023</th>
              <th>Q3 2023</th>
              <th>Q4 2023</th>
              <th>Total 2023</th>
              <th>YoY Growth</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              className="clickable-row"
              onClick={() => openDrillDown && openDrillDown('quarterly-performance', {
                title: 'Containerized Quarterly Performance',
                icon: 'fas fa-box',
                iconColor: '#4da6ff',
                category: 'Historical Performance',
                summary: 'Containerized cargo showed consistent growth throughout 2023, ending with 12.5% YoY increase.',
                details: 'Quarterly progression demonstrates steady market demand and operational efficiency improvements.',
                quarterlyData: [
                  { quarter: 'Q1 2023', volume: '231,800 T', growth: '+10.2%', revenue: '$45.2M' },
                  { quarter: 'Q2 2023', volume: '248,420 T', growth: '+7.2%', revenue: '$48.5M' },
                  { quarter: 'Q3 2023', volume: '255,000 T', growth: '+2.7%', revenue: '$49.8M' },
                  { quarter: 'Q4 2023', volume: '265,000 T', growth: '+3.9%', revenue: '$51.7M' }
                ],
                annualTotals: {
                  'Total Volume': '1,000,220 T',
                  'Total Revenue': '$195.2M',
                  'Average Growth': '+6.05% quarterly',
                  'Market Share Growth': '+3.2%'
                },
                seasonalPattern: 'Q4 typically strongest due to holiday season and inventory buildup',
                growthFactors: [
                  'E-commerce expansion driving Q4 peak',
                  'Manufacturing recovery in Q2-Q3',
                  'Operational improvements throughout year',
                  'New customer acquisitions'
                ],
                performanceHighlights: [
                  'Exceeded annual target by 8.5%',
                  'Maintained market leadership position',
                  'Improved handling efficiency by 15%',
                  'Reduced turnaround time by 2.1 hours'
                ],
                outlook: 'Expected to maintain 10-12% growth in 2024'
              }, 'Containerized Quarterly Analysis')}
            >
              <td><strong>Containerized</strong></td>
              <td>231,800</td>
              <td>248,420</td>
              <td>255,000</td>
              <td>265,000</td>
              <td>1,000,220</td>
              <td><span className="status-indicator status-high">+12.5%</span></td>
            </tr>
            
            <tr 
              className="clickable-row"
              onClick={() => openDrillDown && openDrillDown('quarterly-performance', {
                title: 'Dry Bulk Quarterly Performance',
                icon: 'fas fa-mountain',
                iconColor: '#ff9f40',
                category: 'Historical Performance',
                summary: 'Dry bulk recovered strongly in 2023 with 8.2% annual growth after challenging start.',
                details: 'Performance improved throughout the year with new contracts and market recovery.',
                quarterlyData: [
                  { quarter: 'Q1 2023', volume: '174,120 T', growth: '+8.5%', revenue: '$26.1M' },
                  { quarter: 'Q2 2023', volume: '179,850 T', growth: '+3.3%', revenue: '$27.0M' },
                  { quarter: 'Q3 2023', volume: '182,000 T', growth: '+1.2%', revenue: '$27.3M' },
                  { quarter: 'Q4 2023', volume: '190,000 T', growth: '+4.4%', revenue: '$28.5M' }
                ],
                annualTotals: {
                  'Total Volume': '725,970 T',
                  'Total Revenue': '$108.9M',
                  'Average Growth': '+4.35% quarterly',
                  'Market Share': '33.5%'
                },
                marketRecovery: 'Strong recovery from 2022 decline, driven by mining and agriculture',
                commodityMix: [
                  'Iron Ore: 45% (primary growth driver)',
                  'Coal: 30% (stable performance)',
                  'Grain: 15% (seasonal variations)',
                  'Other: 10% (cement, fertilizers)'
                ],
                strategicSuccesses: [
                  'Secured new iron ore contracts',
                  'Improved bulk handling efficiency',
                  'Expanded storage capacity',
                  'Enhanced customer relationships'
                ],
                challengesOvercome: [
                  'Commodity price volatility',
                  'Weather-related disruptions',
                  'Competition from alternative ports',
                  'Environmental regulations'
                ],
                outlook: 'Expected 8-10% growth in 2024 with new infrastructure projects'
              }, 'Dry Bulk Quarterly Analysis')}
            >
              <td><strong>Dry Bulk</strong></td>
              <td>174,120</td>
              <td>179,850</td>
              <td>182,000</td>
              <td>190,000</td>
              <td>725,970</td>
              <td><span className="status-indicator status-high">+8.2%</span></td>
            </tr>
            
            <tr 
              className="clickable-row"
              onClick={() => openDrillDown && openDrillDown('quarterly-performance', {
                title: 'Liquid Bulk Quarterly Performance',
                icon: 'fas fa-oil-can',
                iconColor: '#ff6384',
                category: 'Historical Performance',
                summary: 'Liquid bulk declined throughout 2023 with -8.2% annual growth, reflecting market transition.',
                details: 'Quarterly decline accelerated as energy transition impacts traditional fossil fuel markets.',
                quarterlyData: [
                  { quarter: 'Q1 2023', volume: '88,340 T', growth: '-5.2%', revenue: '$35.3M' },
                  { quarter: 'Q2 2023', volume: '85,790 T', growth: '-2.9%', revenue: '$34.3M' },
                  { quarter: 'Q3 2023', volume: '82,000 T', growth: '-4.4%', revenue: '$32.8M' },
                  { quarter: 'Q4 2023', volume: '80,000 T', growth: '-2.4%', revenue: '$32.0M' }
                ],
                annualTotals: {
                  'Total Volume': '336,130 T',
                  'Total Revenue': '$134.4M',
                  'Average Decline': '-3.73% quarterly',
                  'Market Share': '15.3%'
                },
                marketFactors: [
                  'Reduced crude oil imports',
                  'Shift to renewable energy',
                  'Geopolitical trade restrictions',
                  'Increased domestic production'
                ],
                commodityAnalysis: [
                  'Crude Oil: 60% (primary decline)',
                  'Chemicals: 25% (stable)',
                  'LNG: 10% (growth area)',
                  'Other: 5% (vegetable oils, etc.)'
                ],
                transitionStrategy: [
                  'Develop LNG handling capabilities',
                  'Diversify chemical shipments',
                  'Explore biofuels market',
                  'Optimize existing infrastructure'
                ],
                financialImpact: 'Revenue decline offset by higher-margin containerized growth',
                recoveryPlan: [
                  'LNG terminal development (2025)',
                  'Chemical storage expansion',
                  'Partnerships with renewable energy companies',
                  'Market diversification'
                ],
                outlook: 'Expected stabilization in 2024 with potential LNG growth'
              }, 'Liquid Bulk Quarterly Analysis')}
            >
              <td><strong>Liquid Bulk</strong></td>
              <td>88,340</td>
              <td>85,790</td>
              <td>82,000</td>
              <td>80,000</td>
              <td>336,130</td>
              <td><span className="status-indicator status-low">-8.2%</span></td>
            </tr>
            
            <tr 
              className="clickable-row"
              onClick={() => openDrillDown && openDrillDown('quarterly-performance', {
                title: 'Break Bulk Quarterly Performance',
                icon: 'fas fa-cubes',
                iconColor: '#4cd964',
                category: 'Historical Performance',
                summary: 'Break bulk showed steady growth throughout 2023 with 5.2% annual increase.',
                details: 'Consistent performance driven by project cargo and specialized shipments.',
                quarterlyData: [
                  { quarter: 'Q1 2023', volume: '26,110 T', growth: '+3.5%', revenue: '$10.4M' },
                  { quarter: 'Q2 2023', volume: '27,770 T', growth: '+6.4%', revenue: '$11.1M' },
                  { quarter: 'Q3 2023', volume: '29,000 T', growth: '+4.4%', revenue: '$11.6M' },
                  { quarter: 'Q4 2023', volume: '30,000 T', growth: '+3.4%', revenue: '$12.0M' }
                ],
                annualTotals: {
                  'Total Volume': '112,880 T',
                  'Total Revenue': '$45.1M',
                  'Average Growth': '+4.43% quarterly',
                  'Market Share': '5.1%'
                },
                cargoTypes: [
                  'Steel: 40% (construction materials)',
                  'Timber: 25% (building supplies)',
                  'Project Cargo: 20% (infrastructure projects)',
                  'Machinery: 15% (industrial equipment)'
                ],
                marketPosition: 'Niche specialist with high-value cargo',
                competitiveAdvantages: [
                  'Specialized handling equipment',
                  'Experienced project cargo team',
                  'Flexible operations',
                  'Strong customer relationships'
                ],
                growthAreas: [
                  'Wind energy components',
                  'Infrastructure project materials',
                  'Oversized industrial equipment',
                  'Specialized manufacturing components'
                ],
                operationalChallenges: [
                  'Specialized handling requirements',
                  'Space constraints for oversized cargo',
                  'Weather sensitivity',
                  'Project timeline dependencies'
                ],
                strategicFocus: [
                  'Develop wind energy cargo expertise',
                  'Expand project cargo capabilities',
                  'Invest in specialized equipment',
                  'Strengtain partnerships with engineering firms'
                ],
                outlook: 'Expected 6-8% growth in 2024 with infrastructure projects'
              }, 'Break Bulk Quarterly Analysis')}
            >
              <td><strong>Break Bulk</strong></td>
              <td>26,110</td>
              <td>27,770</td>
              <td>29,000</td>
              <td>30,000</td>
              <td>112,880</td>
              <td><span className="status-indicator status-medium">+5.2%</span></td>
            </tr>
            
            <tr className="highlight">
              <td><strong>Total</strong></td>
              <td>520,370</td>
              <td>541,830</td>
              <td>548,000</td>
              <td>565,000</td>
              <td>2,175,200</td>
              <td><span className="status-indicator status-high">+7.8%</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CommodityTab;